import type { MapGeometry, MapLocation, MapStand } from "../shared/types";

const standIndices = new WeakMap<MapGeometry, Map<string, MapStand>>();
function standIndex(geometry: MapGeometry) {
  let stands = standIndices.get(geometry);
  if (!stands) {
    stands = new Map(geometry.stands.map((stand) => [stand.id, stand]));
    standIndices.set(geometry, stands);
  }
  return stands;
}

function inside(x: number, y: number, polygon: number[][]) {
  let result = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [ax, ay] = polygon[i],
      [bx, by] = polygon[j];
    if (ay > y !== by > y && x < ((bx - ax) * (y - ay)) / (by - ay) + ax)
      result = !result;
  }
  return result;
}

const boothAnchors = new WeakMap<MapStand, { x: number; y: number }>();

function boothAnchor(stand: MapStand) {
  const cached = boothAnchors.get(stand);
  if (cached) return cached;
  const points = stand.polygons.flat();
  const xs = points.map(([x]) => x),
    ys = points.map(([, y]) => y);
  const right = Math.max(...xs),
    top = Math.min(...ys);
  const inset = Math.min(
    8,
    (right - Math.min(...xs)) * 0.15,
    (Math.max(...ys) - top) * 0.15,
  );
  const preferred = { x: right - inset, y: top + inset };
  if (
    stand.polygons.some((polygon) => inside(preferred.x, preferred.y, polygon))
  ) {
    boothAnchors.set(stand, preferred);
    return preferred;
  }

  // A rotated or concave roof may not occupy its bounding-box corner. Look
  // for a nearby inset point in actual roof spans, independent of the editable
  // navigation position (which may intentionally be in an aisle).
  let nearest: { x: number; y: number } | undefined;
  let nearestDistance = Infinity;
  for (const polygon of stand.polygons) {
    const levels = [...new Set(polygon.map(([, y]) => y))].sort(
      (a, b) => a - b,
    );
    const scans = [
      preferred.y,
      ...levels.slice(1).map((y, index) => (levels[index] + y) / 2),
    ];
    for (const y of scans) {
      const crossings: number[] = [];
      for (let index = 0; index < polygon.length; index++) {
        const [ax, ay] = polygon[index];
        const [bx, by] = polygon[(index + 1) % polygon.length];
        if (ay > y !== by > y)
          crossings.push(ax + ((y - ay) * (bx - ax)) / (by - ay));
      }
      crossings.sort((a, b) => a - b);
      for (let index = 0; index + 1 < crossings.length; index += 2) {
        const left = crossings[index];
        const right = crossings[index + 1];
        const padding = Math.min(inset, (right - left) / 2);
        const x = Math.max(
          left + padding,
          Math.min(right - padding, preferred.x),
        );
        if (!inside(x, y, polygon)) continue;
        const distance = (x - preferred.x) ** 2 + (y - preferred.y) ** 2;
        if (distance < nearestDistance) {
          nearest = { x, y };
          nearestDistance = distance;
        }
      }
    }
  }
  // Valid roof polygons have an interior span. Retain a real roof vertex even
  // for malformed/degenerate in-memory geometry, never an unrelated aisle.
  if (!nearest) {
    const [x, y] = points.reduce((best, point) =>
      (point[0] - preferred.x) ** 2 + (point[1] - preferred.y) ** 2 <
      (best[0] - preferred.x) ** 2 + (best[1] - preferred.y) ** 2
        ? point
        : best,
    );
    nearest = { x, y };
  }
  boothAnchors.set(stand, nearest);
  return nearest;
}

/** A fixed booth-relative corner; entrances retain their saved floor anchors. */
export function venueMapPinAnchor(
  location: Pick<MapLocation, "id" | "x" | "y">,
  geometry: MapGeometry,
) {
  const stand = standIndex(geometry).get(location.id);
  if (!stand) return { x: location.x, y: location.y };
  const anchor = boothAnchor(stand);
  return { x: anchor.x / geometry.width, y: anchor.y / geometry.height };
}
